import { useState, useEffect, useMemo, useCallback } from 'react'
import { debounce } from 'lodash'
import { message } from 'antd'

import TemplatesAPI from "../../../axios/templates/TemplateAPI"
import PhysicianAPI from '../../../axios/physician/PhysicianApi'
import APIHelper from '../../../axios/apiHelper';
import { TemplateTypeEnum } from '../../../types/enums';

export function useTemplates(type, user) {
    const [loading, setLoading] = useState(false)
    const [selectedTemplate, selectTemplate] = useState(null)
    const [templates, setTemplates] = useState([])
    const [telemedicineCategories, setTelemedicineCategories] = useState([])
    const [caseStatuses, setCaseStatues] = useState([])

    const defaultOwner = type === TemplateTypeEnum.VISIT ? null : user?._id;

    const [templateFilters, setTemplateFilters] = useState({
        owner: defaultOwner,
        categoryIds: null,
        status: null,
        search: null,
        sortBy: 'title',
        sortDirection: 'asc',
        telemedicineCategory: 'All',
        caseStatus: 'All',
    });

    useEffect(() => {
        try {   
            APIHelper.fetchLookUp('tele-medicine-category')
                .then(({ data }) => setTelemedicineCategories(data.payload))
            APIHelper.fetchLookUp('case-status')
                .then(({ data }) => setCaseStatues(data.payload))
        } catch (e) {
            console.error(e)
        }
    }, [])

    const searchTemplates = useCallback(async ({ ...params }) => {
        setLoading(true)
        setTemplates([])

        try {
            const { data: { templates } } = await TemplatesAPI.getMany(params)

            if (!templates?.length) return setTemplates([])

            const mappedTemplates = templates.map(template => {
                const additions = {
                    teleMedicineCategories: template.teleMedicineCategoryIds?.map(category => category.name).join(', '),
                    ...template.statusId && { caseStatus: template.statusId.name }
                }

                Object.assign(template, additions)
                return template
            })

            setTemplates(mappedTemplates)
        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false)
        }
    }, [])

    const debouncedSearchTemplates = useMemo(() => debounce(params => searchTemplates({ ...params }), 1000), [searchTemplates])

    const optimisticEdit = async (templateId, values, { onSuccess } = {}) => {
        const existing = templates.find(t => t._id === templateId)

        const updatedCategories = telemedicineCategories.filter(c =>
            values.teleMedicineCategoryIds?.includes(c._id)
        )
        const updatedStatus = caseStatuses.find(s => s._id === values.statusId)

        const optimistic = {
            ...existing,
            title: values.title,
            message: values.message,
            teleMedicineCategoryIds: updatedCategories,
            teleMedicineCategories: updatedCategories.map(c => c.name).join(', '),
            ...(updatedStatus && { statusId: updatedStatus, caseStatus: updatedStatus.name }),
        }

        setTemplates(prev => prev.map(t => t._id === templateId ? optimistic : t))
        onSuccess?.()

        try {
            await PhysicianAPI.updateTemplate(templateId, values)
        } catch (err) {
            setTemplates(prev => prev.map(t => t._id === templateId ? existing : t))
            message.error({ content: err.response?.data?.message || 'Error updating template' })
        }
    }

    const optimisticDelete = async (templateId, { onSuccess } = {}) => {
        const existing = templates.find(t => t._id === templateId)
        const originalIndex = templates.findIndex(t => t._id === templateId)

        setTemplates(prev => prev.filter(t => t._id !== templateId))
        onSuccess?.()

        try {
            await PhysicianAPI.deleteTemplate(templateId)
        } catch (err) {
            setTemplates(prev => {
                const restored = [...prev]
                restored.splice(originalIndex, 0, existing)
                return restored
            })
            message.error({ content: err.response?.data?.message || 'Error deleting template' })
        }
    }

    const clearTemplates = () => setTemplates([])

    const resetTemplates = () => {
        setTemplateFilters({
            owner: type === TemplateTypeEnum.VISIT ? null : user?._id,
            categoryIds: null,
            status: null,
            search: null,
            sortBy: 'title',
            sortDirection: 'asc',
            telemedicineCategory: 'All',
            caseStatus: 'All',
          })
        setTemplates([])
    }

    useEffect(() => {
        // telemedicineCategory and caseStatus are client-side filter fields only; strip before API call
        const { telemedicineCategory, caseStatus, ...apiFilters } = templateFilters
        setTemplates([])
        searchTemplates({ type, ...apiFilters })
    }, [templateFilters, searchTemplates])

    useEffect(() => {
        resetTemplates()
    }, [type])

    return {
        selectTemplate,
        clearTemplates,
        optimisticEdit,
        optimisticDelete,
        setTemplateFilters,
        searchTemplates: debouncedSearchTemplates,
        telemedicineCategories,
        caseStatuses,
        selectedTemplate,
        loading,
        templates,
        templateFilters
    };
}
